import { ResourceClient } from "@dl/web-core-js/api";
import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import {
  Badge,
  Button,
  Card,
  Dialog,
  EditPane,
  Field,
  Input,
  MenuButton,
  Select,
  Textarea,
  toast,
  useConfirm,
  type EditPaneTab,
} from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Hourglass, MoreHorizontal, PackageCheck, PauseCircle, PlayCircle, Printer, Send } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage, type UnitDTO } from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import {
  lineTotal,
  PurchaseOrderClient,
  PurchaseOrderLineClient,
  type PurchaseOrderDTO,
  type PurchaseOrderLineDTO,
} from "../../api/purchasing";
import { fetchSingleRow, SETTINGS_PATHS, settingsKeys, type PurchasingSettingsDTO } from "../../api/settings";
import { SupplierLink } from "../../components/SupplierLink";
import { NexpimView } from "../../views";
import { PartPicker } from "../manufacturing/PartPicker";
import { StockMovementsGrid } from "../manufacturing/StockMovementsGrid";
import { HoldBadge, OriginBadge, ProgressBadge, StatusBadge, useStatusLabel } from "./badges";
import { ReceiveDialog } from "./ReceiveDialog";
import { formatCad, formatMoney, formatPrice } from "./replenishment/shared";
import { isoDate } from "../../api/reporting";

/** Every status move the page can ask for. Each is one POST on the order. */
type OrderAction = "issue" | "issue-on-hold" | "approve" | "release" | "hold" | "close" | "cancel" | "reopen";

interface PartSupplierRow {
  id: number;
  part_id: number;
  supplier_id: number;
  priority: number;
  supplier_part_number: string | null;
  price: number | null;
  order_unit_id?: number | null;
  order_unit_kind?: "PER_PART" | "SHARED" | null;
  stock_units_per_order_unit?: number | null;
}

/**
 * One purchase order: its lines, its header, and what has arrived.
 *
 * <p>What can be done follows the status, the same rules the server enforces:
 * a draft is written and issued — straight to the supplier, or to the approver
 * when its total reaches the purchasing threshold; an order awaiting approval
 * is approved or sent back with a note, and its lines are frozen meanwhile; an
 * issued order is received against, held or released, closed, or — if nothing
 * has arrived — cancelled; a closed one can be reopened for the late delivery
 * nobody expected. Lines are editable while the order is a draft or issued.</p>
 *
 * <p>"On hold" means the supplier has the order but is not to ship yet —
 * usually while a truck fills. Lead time counts from the release, which is why
 * the release date is shown beside the issue date.</p>
 */
export function PurchaseOrderDetailPage({ orderId }: { orderId: number }) {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.PURCHASING);

  const orders = useMemo(() => new PurchaseOrderClient(http), [http]);
  const lines = useMemo(() => new PurchaseOrderLineClient(http), [http]);
  const [receiving, setReceiving] = useState(false);
  const [sendingBack, setSendingBack] = useState(false);
  // The Order tab's unsaved edits live here: the tab is unmounted when another
  // one is shown, and its own state would go with it.
  const [headerEdits, setHeaderEdits] = useState<HeaderEdits>({});
  useEffect(() => setHeaderEdits({}), [orderId]);
  const statusLabel = useStatusLabel();

  const order = useQuery({
    queryKey: ["purchasing", "purchase-order", orderId],
    queryFn: () => orders.findById(orderId),
    enabled: allowed,
  });
  const orderLines = useQuery({
    queryKey: ["purchasing", "purchase-order-line", orderId],
    queryFn: async () =>
      (await lines.find({ page: 0, size: 1000, filters: { purchase_order_id: [String(orderId)] }, sort: ["position,asc"] })).content,
    enabled: allowed,
  });
  const approver = useQuery({
    queryKey: ["purchasing", "can-approve"],
    queryFn: () => orders.canApprove(),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });
  const settings = useQuery({
    queryKey: settingsKeys.purchasing,
    queryFn: () => fetchSingleRow<PurchasingSettingsDTO>(http, SETTINGS_PATHS.purchasing),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });
  const units = useQuery({
    queryKey: ["unit", "all"],
    queryFn: () => new ResourceClient<UnitDTO>(http, "/unit").findAll(),
    staleTime: 30 * 60_000,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["purchasing"] });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "stock-movement"] });
    await queryClient.invalidateQueries({ queryKey: ["replenishment"] });
  };

  const act = useMutation({
    mutationFn: (action: OrderAction) => {
      switch (action) {
        case "issue":
          return orders.issue(orderId);
        case "issue-on-hold":
          return orders.issue(orderId, true);
        case "approve":
          return orders.approve(orderId);
        default:
          return orders[action](orderId);
      }
    },
    onSuccess: async (updated, action) => {
      await refresh();
      if (action === "hold") {
        toast.success(t("po.now-held", "Order {{n}} is on hold: the supplier waits for the release.", { n: updated.number }));
      } else if (action === "release") {
        toast.success(t("po.now-released", "Order {{n}} is released: the supplier can ship.", { n: updated.number }));
      } else if (updated.status === "AWAITING_APPROVAL") {
        toast.success(t("po.now-awaiting", "Order {{n}} is at or above the approval threshold: it waits for the approver.", { n: updated.number }));
      } else {
        toast.success(t("po.now", "Order {{n}} is now {{status}}", { n: updated.number, status: statusLabel(updated.status).toLowerCase() }));
      }
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const remove = useMutation({
    mutationFn: () => orders.remove(orderId),
    onSuccess: async () => {
      await refresh();
      void navigate({ to: "/purchasing/purchase-orders" });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.purchase-orders", "Purchase orders")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const data = order.data;
  if (!data) {
    return (
      <>
        <PageHeader
          parent={{ to: "/purchasing/purchase-orders", label: t("navigation.item.purchase-orders", "Purchase orders") }}
          title={t("navigation.item.purchase-orders", "Purchase orders")}
        />
        <div className="p-4 text-[13px] text-ink-muted lg:p-6">
          {order.isLoading ? t("common.loading", "Loading…") : t("po.not-found", "That order could not be loaded.")}
        </div>
      </>
    );
  }

  const editable = canWrite && (data.status === "DRAFT" || data.status === "ISSUED");
  const canApprove = approver.data === true;
  const threshold = settings.data?.approval_threshold_amount;
  const rows = orderLines.data ?? [];
  const total = rows.reduce((sum, line) => sum + lineTotal(line), 0) + Number(data.shipping_cost ?? 0);

  const headerDirty =
    Object.keys(headerEdits).some((key) => key !== "shipping") ||
    (headerEdits.shipping !== undefined && Number(headerEdits.shipping) !== Number(data.shipping_cost ?? 0));
  /** Issuing sends the saved order: unsaved header edits would silently stay behind. */
  const issue = async (action: "issue" | "issue-on-hold") => {
    if (
      headerDirty &&
      !(await confirm({
        title: t("po.unsaved.title", "Issue without your changes?"),
        message: t("po.unsaved.message", "The Order tab has changes that are not saved. The order goes out as it was last saved."),
        confirmLabel: t("po.issue", "Issue"),
      }))
    ) {
      return;
    }
    act.mutate(action);
  };

  const ask = async (action: "close" | "cancel", title: string, message: string) => {
    if (await confirm({ title, message, confirmLabel: title, variant: action === "cancel" ? "danger" : "primary" })) {
      act.mutate(action);
    }
  };

  const tabs: EditPaneTab[] = [
    {
      id: "lines",
      flush: true,
      label: t("po.tab.lines", "Lines"),
      badge: rows.filter((line) => line.kind !== "TEXT").length,
      content: (
        <LinesTab
          order={data}
          rows={rows}
          units={units.data ?? []}
          editable={editable}
          onChanged={refresh}
        />
      ),
    },
    {
      id: "order",
      label: headerDirty ? `${t("po.tab.order", "Order")} *` : t("po.tab.order", "Order"),
      content: <HeaderTab order={data} edits={headerEdits} onEdit={setHeaderEdits} editable={editable} onSaved={refresh} />,
    },
    {
      id: "receipts",
      flush: true,
      label: t("po.tab.receipts", "Receptions"),
      content: <StockMovementsGrid purchaseOrderId={orderId} />,
    },
  ];

  return (
    <>
      <PageHeader
        parent={{ to: "/purchasing/purchase-orders", label: t("navigation.item.purchase-orders", "Purchase orders") }}
        title={t("po.title", "Order {{n}}", { n: data.number })}
        subtitle={
          <>
            <SupplierLink id={data.supplier_id}>{data.supplier_name ?? ""}</SupplierLink>
            {data.warehouse_code ? ` · ${data.warehouse_code}` : ""}
          </>
        }
        badges={
          <>
            <StatusBadge status={data.status} />
            {data.status !== "DRAFT" && <ProgressBadge progress={data.receipt_progress} />}
            <HoldBadge onHold={data.status === "ISSUED" && data.is_on_hold} />
            <OriginBadge origin={data.origin} />
            <Badge variant="neutral" size="sm">
              {formatMoney(total)} {data.currency}
              {data.currency !== "CAD" && data.total_cad != null ? ` ≈ ${formatCad(data.total_cad)}` : ""}
            </Badge>
            {data.sigm_purchase_order_id != null && (
              <Badge variant="neutral" size="sm">
                SIGM
              </Badge>
            )}
          </>
        }
        actions={
          <>
            <MenuButton
              label={t("po.more", "More")}
              variant="ghost"
              icon={<MoreHorizontal className="size-4" />}
              disabled={act.isPending}
              items={[
                {
                  label: t("po.print", "Print"),
                  icon: <Printer className="size-4" />,
                  onClick: () => void navigate({ to: "/purchasing/purchase-orders/$orderId/print", params: { orderId: String(orderId) } }),
                },
                ...(canWrite && data.status === "DRAFT"
                  ? [
                      ...(rows.some((line) => line.kind !== "TEXT")
                        ? [
                            {
                              label: t("po.issue.on-hold", "Issue on hold: the supplier waits for the release"),
                              icon: <PauseCircle className="size-4" />,
                              onClick: () => void issue("issue-on-hold"),
                            },
                          ]
                        : []),
                      {
                        label: t("po.delete", "Delete"),
                        onClick: () => {
                          void (async () => {
                            if (
                              await confirm({
                                title: t("po.delete.title", "Delete this draft?"),
                                message: t("po.delete.message", "A draft has not been sent anywhere, so it can go entirely."),
                                variant: "danger",
                                confirmLabel: t("po.delete", "Delete"),
                              })
                            ) {
                              remove.mutate();
                            }
                          })();
                        },
                      },
                    ]
                  : []),
                ...(canWrite && data.status === "ISSUED"
                  ? [
                      ...(!data.is_on_hold && data.receipt_progress === "NONE"
                        ? [
                            {
                              label: t("po.hold", "Hold"),
                              icon: <PauseCircle className="size-4" />,
                              onClick: () => act.mutate("hold"),
                            },
                          ]
                        : []),
                      {
                        label: t("po.close", "Close"),
                        onClick: () =>
                          void ask("close", t("po.close", "Close"), t("po.close.message", "Nothing more will be expected on this order, whatever is still outstanding.")),
                      },
                      ...(data.receipt_progress === "NONE"
                        ? [
                            {
                              label: t("po.cancel", "Cancel order"),
                              onClick: () =>
                                void ask("cancel", t("po.cancel", "Cancel order"), t("po.cancel.message", "Nothing has arrived against it. It stays on file as cancelled.")),
                            },
                          ]
                        : []),
                    ]
                  : []),
              ]}
            />
            {canWrite && data.status === "DRAFT" && (
              <Button
                variant="primary"
                loading={act.isPending}
                disabled={!orderLines.data || rows.every((line) => line.kind === "TEXT")}
                title={rows.every((line) => line.kind === "TEXT") ? t("po.issue.empty", "Add a line first: an order with nothing on it cannot be issued.") : undefined}
                onClick={() => void issue("issue")}
              >
                <Send className="size-4" />
                {t("po.issue", "Issue")}
              </Button>
            )}
            {canWrite && data.status === "AWAITING_APPROVAL" && canApprove && (
              <>
                <Button variant="ghost" onClick={() => setSendingBack(true)}>
                  {t("po.send-back", "Send back")}
                </Button>
                <Button variant="primary" loading={act.isPending} onClick={() => act.mutate("approve")}>
                  {t("po.approve", "Approve")}
                </Button>
              </>
            )}
            {canWrite && data.status === "ISSUED" && (
              <>
                {data.is_on_hold && (
                  <Button variant="secondary" loading={act.isPending} onClick={() => act.mutate("release")}>
                    <PlayCircle className="size-4" />
                    {t("po.release", "Release")}
                  </Button>
                )}
                <Button variant="primary" onClick={() => setReceiving(true)}>
                  <PackageCheck className="size-4" />
                  {t("po.receive", "Receive")}
                </Button>
              </>
            )}
            {canWrite && data.status === "CLOSED" && (
              <Button variant="ghost" loading={act.isPending} onClick={() => act.mutate("reopen")}>
                {t("po.reopen", "Reopen")}
              </Button>
            )}
          </>
        }
      />
      {data.status === "AWAITING_APPROVAL" && (
        <div className="flex items-start gap-3 border-b border-line bg-warn-soft px-4 py-3 text-[13px] lg:px-6">
          <Hourglass className="mt-0.5 size-4 shrink-0 text-warn" />
          <div className="min-w-0">
            <p className="font-medium text-ink">
              {threshold != null
                ? t("po.awaiting.banner", "Waiting for approval: its total reaches the {{threshold}} threshold.", {
                    threshold: formatCad(threshold),
                  })
                : t("po.awaiting.banner-plain", "Waiting for approval.")}
            </p>
            <p className="text-ink-muted">
              {canApprove
                ? t("po.awaiting.approver", "Approve it to send it to the supplier, or send it back to draft saying what to change.")
                : t("po.awaiting.others", "Its lines are frozen until the approver approves it or sends it back.")}
            </p>
          </div>
        </div>
      )}
      {data.status === "DRAFT" && data.approval_note && (
        <div className="border-b border-line bg-info-soft px-4 py-3 text-[13px] lg:px-6">
          <span className="font-medium text-ink">{t("po.sent-back", "Sent back by the approver:")}</span>{" "}
          <span className="text-ink-muted">{data.approval_note}</span>
        </div>
      )}
      <EditPane tabs={tabs} urlParam="tab" />
      <SendBackDialog
        open={sendingBack}
        orderId={orderId}
        onClose={() => setSendingBack(false)}
        onDone={async () => {
          setSendingBack(false);
          await refresh();
        }}
      />
      {receiving && (
        <ReceiveDialog
          order={data}
          lines={rows}
          onClose={() => setReceiving(false)}
          onPosted={async () => {
            await refresh();
            await queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
          }}
        />
      )}
    </>
  );
}

function LinesTab({
  order,
  rows,
  units,
  editable,
  onChanged,
}: {
  order: PurchaseOrderDTO;
  rows: PurchaseOrderLineDTO[];
  units: UnitDTO[];
  editable: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const lines = useMemo(() => new PurchaseOrderLineClient(http), [http]);
  const partSuppliers = useMemo(() => new ResourceClient<PartSupplierRow>(http, "/manufacturing/part-supplier"), [http]);
  const unitById = useMemo(() => new Map(units.map((unit) => [unit.id, unit])), [units]);
  const nextPosition = Math.max(0, ...rows.map((line) => line.position)) + 10;
  // One line at a time: a double click must not add the part, or the expense, twice.
  // The ref is the guard (two clicks in one frame both see the same state);
  // the state only greys the buttons.
  const busy = useRef(false);
  const [adding, setAdding] = useState(false);
  const guarded = (work: () => Promise<void>) => async () => {
    if (busy.current) return;
    busy.current = true;
    setAdding(true);
    try {
      await work();
    } finally {
      busy.current = false;
      setAdding(false);
    }
  };

  /** A new line defaults its price and the supplier's own number from what this supplier charges for it. */
  const addPart = (part: { id: number; number: string; name: string }) => guarded(async () => {
    try {
      const known = (
        await partSuppliers.find({
          page: 0,
          size: 1,
          filters: { part_id: [String(part.id)], supplier_id: [String(order.supplier_id)] },
          sort: ["priority,asc"],
        })
      ).content[0];
      // Bought by the barrel or the roll from this supplier: the line is in those, at their price.
      const perUnit =
        known?.order_unit_kind === "PER_PART" && Number(known.stock_units_per_order_unit ?? 0) > 0 ? Number(known.stock_units_per_order_unit) : null;
      await lines.create({
        id: 0,
        purchase_order_id: order.id,
        position: nextPosition,
        kind: "ITEM",
        part_id: part.id,
        supplier_part_number: known?.supplier_part_number ?? null,
        description: null,
        quantity: 1,
        unit_id: null,
        stock_units_per_unit: perUnit ?? 1,
        order_unit_id: perUnit != null ? (known?.order_unit_id ?? null) : null,
        unit_price: Number(known?.price ?? 0) * (perUnit ?? 1),
        required_date: null,
        quantity_cancelled: 0,
        quantity_delivered: 0,
        note: null,
      });
      await onChanged();
      if (!known) {
        toast.message(t("po.line.no-price", "This supplier has no price on file for {{part}}; set one on the line.", { part: part.number }));
      }
    } catch (failure) {
      toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
    }
  })();

  /** Spending that never enters stock: a repair, a parcel, a rental. Delivered, not received. */
  const addExpense = guarded(async () => {
    try {
      await lines.create({
        id: 0,
        purchase_order_id: order.id,
        position: nextPosition,
        kind: "EXPENSE",
        part_id: null,
        supplier_part_number: null,
        description: t("po.line.expense-default", "Describe what is bought"),
        quantity: 1,
        unit_id: null,
        stock_units_per_unit: 1,
        unit_price: 0,
        required_date: null,
        quantity_cancelled: 0,
        quantity_delivered: 0,
        note: null,
      });
      await onChanged();
    } catch (failure) {
      toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
    }
  });

  const addText = guarded(async () => {
    try {
      await lines.create({
        id: 0,
        purchase_order_id: order.id,
        position: nextPosition,
        kind: "TEXT",
        part_id: null,
        supplier_part_number: null,
        description: t("po.line.text-default", "Note to the supplier"),
        quantity: 0,
        unit_id: null,
        stock_units_per_unit: 1,
        unit_price: 0,
        required_date: null,
        quantity_cancelled: 0,
        quantity_delivered: 0,
        note: null,
      });
      await onChanged();
    } catch (failure) {
      toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
    }
  });

  const cols = useMemo(() => {
    const c = defineColumns<PurchaseOrderLineDTO>();
    return [
      c.number("position", (row) => row.position, { headerKey: "column.bom.position", priority: 4 }),
      c.custom(
        "part",
        (row) => row.part_number ?? row.description ?? "",
        (row) =>
          row.kind === "TEXT" ? (
            <span className="italic text-ink-muted">{row.description}</span>
          ) : row.kind === "EXPENSE" ? (
            <span>
              {row.description}
              <Badge variant="neutral" size="sm" className="ml-2">
                {t("po.line.expense", "Expense")}
              </Badge>
            </span>
          ) : (
            <span>
              {row.part_number}
              <span className="ml-2 text-[11px] text-ink-faint">{row.part_name}</span>
              {row.supplier_part_number && (
                <span className="ml-2 text-[11px] text-ink-faint">({row.supplier_part_number})</span>
              )}
            </span>
          ),
        { headerKey: "column.bom.component", priority: 1 },
      ),
      c.custom("quantity", (row) => (row.kind === "TEXT" ? null : Number(row.quantity)), (row) => (row.kind === "TEXT" ? "" : formatNumber(row.quantity)), {
        headerKey: "column.bom.quantity",
        filter: "range",
        align: "right",
        priority: 2,
      }),
      c.text("unit", (row) => (row.kind === "TEXT" ? "" : row.order_unit_code ? row.order_unit_code : row.unit_id === null ? "" : (unitById.get(row.unit_id)?.code ?? "")), {
        headerKey: "column.bom.unit",
      }),
      c.custom("unit_price", (row) => (row.kind === "TEXT" ? null : Number(row.unit_price)), (row) => (row.kind === "TEXT" ? "" : formatPrice(row.unit_price)), {
        headerKey: "column.po.price",
        filter: "range",
        align: "right",
        priority: 3,
      }),
      c.custom("total", (row) => (row.kind === "TEXT" ? null : lineTotal(row)), (row) => (row.kind === "TEXT" ? "" : formatMoney(lineTotal(row))), {
        headerKey: "column.po.line-total",
        filter: "range",
        align: "right",
      }),
      c.custom(
        "received",
        (row) => row.quantity_received ?? 0,
        (row) =>
          row.kind === "TEXT" ? (
            <span />
          ) : (
            <span className="tabular-nums">
              {formatNumber(row.quantity_received)}
              {Number(row.quantity_outstanding ?? 0) > 0 && (
                <span className="ml-1.5 text-[11px] text-warn">
                  {t("po.line.outstanding", "{{n}} to come", { n: formatNumber(row.quantity_outstanding) })}
                </span>
              )}
            </span>
          ),
        { headerKey: "column.po.received", filter: "range", align: "right", priority: 2 },
      ),
      c.text("quantity_delivered", (row) => (row.kind === "EXPENSE" ? formatNumber(row.quantity_delivered) : ""), {
        headerKey: "column.po.delivered",
        defaultVisible: false,
      }),
      c.text("quantity_cancelled", (row) => (Number(row.quantity_cancelled) > 0 ? formatNumber(row.quantity_cancelled) : ""), {
        headerKey: "column.po.cancelled",
        defaultVisible: false,
      }),
      c.text("stock_units_per_unit", (row) => (row.kind === "TEXT" ? "" : formatNumber(row.stock_units_per_unit)), {
        headerKey: "column.po.factor",
        defaultVisible: false,
      }),
      c.text("required_date", (row) => row.required_date ?? "", { headerKey: "column.po.required-date", defaultVisible: false }),
      c.custom(
        "promised_date",
        (row) => row.promised_date ?? "",
        (row) =>
          row.kind === "TEXT" ? (
            <span />
          ) : row.promised_date ? (
            <span
              className={
                Number(row.quantity_outstanding ?? 0) > 0 && row.promised_date < isoDate(new Date())
                  ? "font-medium text-risk"
                  : ""
              }
            >
              {row.promised_date}
            </span>
          ) : (
            <span className="text-ink-faint"></span>
          ),
        { headerKey: "column.po.promised-date", priority: 3 },
      ),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", defaultVisible: false }),
    ];
  }, [t, unitById]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {editable && (
        <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2 lg:px-6">
          <div className="w-full max-w-md">
            <PartPicker
              onPick={(part) => void addPart(part)}
              placeholder={t("po.line.add", "Add a part to this order")}
              exclude={rows.flatMap((line) => (line.part_id === null ? [] : [line.part_id]))}
            />
          </div>
          <Button variant="ghost" size="sm" disabled={adding} onClick={() => void addExpense()}>
            {t("po.line.add-expense", "Add an expense line")}
          </Button>
          <Button variant="ghost" size="sm" disabled={adding} onClick={() => void addText()}>
            {t("po.line.add-text", "Add a note line")}
          </Button>
        </div>
      )}
      <DataTable<PurchaseOrderLineDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        editor={
          editable
            ? {
                fields: {
                  position: {
                    kind: "number",
                    get: (row) => row.position,
                    set: (row, value) => ({ ...row, position: Number(value) }),
                    required: true,
                  },
                  part: {
                    kind: "text",
                    get: (row) => (row.kind === "ITEM" ? row.supplier_part_number : row.description),
                    set: (row, value) =>
                      row.kind === "ITEM"
                        ? { ...row, supplier_part_number: value === "" ? null : value }
                        : { ...row, description: value },
                    placeholder: t("po.line.supplier-number", "Supplier's number"),
                  },
                  quantity: {
                    kind: "number",
                    get: (row) => row.quantity,
                    set: (row, value) => ({ ...row, quantity: Number(value) }),
                  },
                  unit: {
                    kind: "select",
                    get: (row) => (row.unit_id === null ? "" : String(row.unit_id)),
                    set: (row, value) => ({ ...row, unit_id: value === "" ? null : Number(value) }),
                    options: units.filter((unit) => unit.is_active).map((unit) => ({ value: String(unit.id), label: unit.code })),
                  },
                  unit_price: {
                    kind: "number",
                    get: (row) => row.unit_price,
                    set: (row, value) => ({ ...row, unit_price: Number(value) }),
                  },
                  // An expense delivery is corrected here: it never reached stock,
                  // so there is no movement to reverse instead.
                  quantity_delivered: {
                    kind: "number",
                    get: (row) => row.quantity_delivered,
                    set: (row, value) => (row.kind === "EXPENSE" ? { ...row, quantity_delivered: Number(value) } : row),
                  },
                  quantity_cancelled: {
                    kind: "number",
                    get: (row) => row.quantity_cancelled,
                    set: (row, value) => ({ ...row, quantity_cancelled: Number(value) }),
                  },
                  stock_units_per_unit: {
                    kind: "number",
                    get: (row) => row.stock_units_per_unit,
                    set: (row, value) => ({ ...row, stock_units_per_unit: Number(value) }),
                  },
                  required_date: {
                    kind: "text",
                    get: (row) => row.required_date,
                    set: (row, value) => ({ ...row, required_date: value === "" ? null : value }),
                    placeholder: "YYYY-MM-DD",
                  },
                  // The supplier's word. Planning and the cash projection trust it
                  // over the required date, so it is worth typing in on the ack.
                  promised_date: {
                    kind: "text",
                    get: (row) => row.promised_date ?? null,
                    set: (row, value) => ({ ...row, promised_date: value === "" ? null : value }),
                    placeholder: "YYYY-MM-DD",
                  },
                  note: {
                    kind: "text",
                    get: (row) => row.note,
                    set: (row, value) => ({ ...row, note: value === "" ? null : value }),
                  },
                },
                validate: (row) => validateLine(row, t),
                onSave: async (row) => {
                  try {
                    await lines.update(row);
                    await onChanged();
                    toast.success(t("notification.saved", "Saved"));
                  } catch (failure) {
                    // The editor toasts what it catches; hand it the server's
                    // sentence — "awaiting approval; send it back…" — not a status code.
                    throw new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                  }
                },
                onDelete: async (row) => {
                  // Received goods are in the ledger against this line; the
                  // line stays, and what will not come is cancelled instead.
                  if (Number(row.quantity_received ?? 0) > 0) {
                    throw new Error(
                      t("po.line.delete-received", "Part of this line has arrived, so it stays. Set what will not come as cancelled instead."),
                    );
                  }
                  try {
                    await lines.remove(row.id);
                  } catch (failure) {
                    throw new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                  }
                  await onChanged();
                  toast.success(t("notification.deleted", "Deleted"));
                },
                deleteConfirm: () => ({ title: t("bom.line.delete", "Remove this line?") }),
              }
            : undefined
        }
        emptyMessage={t("po.lines.empty", "Nothing on this order yet.")}
      />
    </div>
  );
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A date typed as text: empty, or a real YYYY-MM-DD day (not 2026-02-30). */
function isDay(value: string | null | undefined): boolean {
  if (!value) return true;
  if (!ISO_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/**
 * What the line editor refuses before the server is asked (called on save only): a cleared number
 * input arrives as 0, and a typed date as any text, so both are checked here
 * rather than saved as a silent zero or a date the server rejects.
 */
function validateLine(row: PurchaseOrderLineDTO, t: (key: string, fallback: string) => string): Record<string, string> | null {
  const errors: Record<string, string> = {};
  if (!Number.isInteger(Number(row.position)) || Number(row.position) < 0) {
    errors.position = t("po.line.error.position", "The line number is a whole number, zero or more.");
  }
  if (row.kind !== "TEXT") {
    if (!(Number(row.quantity) > 0)) errors.quantity = t("po.line.error.quantity", "The quantity must be above zero.");
    if (!(Number(row.unit_price) >= 0)) errors.unit_price = t("po.line.error.price", "The price cannot be negative.");
    if (!(Number(row.stock_units_per_unit) > 0)) errors.stock_units_per_unit = t("po.line.error.factor", "The stock units per unit must be above zero.");
    const cancelled = Number(row.quantity_cancelled);
    if (!(cancelled >= 0) || cancelled > Number(row.quantity)) {
      errors.quantity_cancelled = t("po.line.error.cancelled", "The cancelled quantity is between zero and the quantity ordered.");
    }
    if (!(Number(row.quantity_delivered) >= 0)) errors.quantity_delivered = t("po.line.error.delivered", "The delivered quantity cannot be negative.");
  }
  if (row.kind === "TEXT" && (row.description ?? "").trim() === "") {
    errors.part = t("po.line.error.text", "Write the note, or delete the line.");
  }
  if (!isDay(row.required_date)) errors.required_date = t("po.line.error.required-date", "The required date is a date as YYYY-MM-DD.");
  if (!isDay(row.promised_date)) errors.promised_date = t("po.line.error.promised-date", "The promised date is a date as YYYY-MM-DD.");
  const messages = Object.values(errors);
  if (messages.length === 0) return null;
  // The editor only outlines the cell, and a cell in a hidden column (cancelled,
  // factor) has no outline to show: say it as well.
  toast.error(messages.join(" "));
  return errors;
}

/** The header fields a person edits, held by the page so switching tabs does not drop them. */
type HeaderField = "warehouse_id" | "currency" | "order_date" | "required_date" | "supplier_reference" | "shipping_cost" | "note";
export type HeaderEdits = Partial<Pick<PurchaseOrderDTO, HeaderField>> & {
  /** Typed as text so it can be cleared and retyped; a number would turn the empty field into a silent 0. */
  shipping?: string;
};

function HeaderTab({
  order,
  edits,
  onEdit,
  editable,
  onSaved,
}: {
  order: PurchaseOrderDTO;
  edits: HeaderEdits;
  onEdit: (edits: HeaderEdits) => void;
  editable: boolean;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const orders = useMemo(() => new PurchaseOrderClient(http), [http]);
  // Edits lie over the latest order rather than over a copy of it, so a
  // refetch never resurrects old values and an action taken meanwhile (issue,
  // hold) is not undone by the save.
  const { shipping: typedShipping, ...fields } = edits;
  const draft: PurchaseOrderDTO = { ...order, ...fields };
  const shipping = typedShipping ?? String(Number(order.shipping_cost ?? 0));
  const setDraft = (next: PurchaseOrderDTO) => {
    const changed: HeaderEdits = typedShipping === undefined ? {} : { shipping: typedShipping };
    for (const key of ["warehouse_id", "currency", "order_date", "required_date", "supplier_reference", "shipping_cost", "note"] as const) {
      if (next[key] !== order[key]) (changed as Record<string, unknown>)[key] = next[key];
    }
    onEdit(changed);
  };
  const dirty = Object.keys(fields).length > 0 || (typedShipping !== undefined && Number(typedShipping) !== Number(order.shipping_cost ?? 0));
  const shippingValid = shipping.trim() !== "" && Number(shipping) >= 0;
  const orderDateValid = Boolean(draft.order_date);
  const requiredValid = !draft.required_date || !draft.order_date || draft.required_date >= draft.order_date;

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });

  const save = useMutation({
    mutationFn: () => orders.update(draft),
    onSuccess: async () => {
      onEdit({});
      await onSaved();
      toast.success(t("notification.saved", "Saved"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const text = (value: string) => (value.trim() === "" ? null : value);

  return (
    <Card className="p-4 sm:p-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label={t("column.po.supplier", "Supplier")} htmlFor="po-supplier">
          {/* Never edited here, so shown as what it is: a way to the supplier. */}
          <div id="po-supplier" className="flex h-9 w-full min-w-0 items-center gap-1.5 rounded-md border border-line bg-surface-sunk px-3 text-sm max-md:h-11">
            <SupplierLink id={order.supplier_id} className="truncate">
              {order.supplier_name ?? ""}
            </SupplierLink>
            {order.supplier_code && <span className="shrink-0 text-[12px] text-ink-muted">{order.supplier_code}</span>}
          </div>
        </Field>
        <Field label={t("po.receive-into", "Receive into")} htmlFor="po-warehouse">
          <Select
            id="po-warehouse"
            value={String(draft.warehouse_id)}
            disabled={!editable}
            options={(warehouses.data ?? [])
              .filter((w) => (w.is_active && w.kind !== "DISTRIBUTION") || w.id === draft.warehouse_id)
              .map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }))}
            onChange={(event) => setDraft({ ...draft, warehouse_id: Number(event.target.value) })}
          />
        </Field>
        <Field label={t("column.currency", "Currency")} htmlFor="po-currency">
          <Select
            id="po-currency"
            value={draft.currency}
            disabled={!editable || order.status !== "DRAFT"}
            options={[
              { value: "CAD", label: "CAD" },
              { value: "USD", label: "USD" },
            ]}
            onChange={(event) => setDraft({ ...draft, currency: event.target.value as PurchaseOrderDTO["currency"] })}
          />
        </Field>
        <Field
          label={t("column.po.order-date", "Order date")}
          htmlFor="po-date"
          required={editable}
          error={editable && !orderDateValid ? t("po.order-date.required", "An order has a date.") : undefined}
        >
          <Input
            id="po-date"
            type="date"
            value={draft.order_date ?? ""}
            disabled={!editable}
            onChange={(event) => setDraft({ ...draft, order_date: event.target.value || null })}
          />
        </Field>
        <Field
          label={t("column.po.required-date", "Required")}
          htmlFor="po-required"
          error={editable && !requiredValid ? t("po.required-date.before", "Cannot be before the order date.") : undefined}
        >
          <Input
            id="po-required"
            type="date"
            value={draft.required_date ?? ""}
            disabled={!editable}
            onChange={(event) => setDraft({ ...draft, required_date: event.target.value || null })}
          />
        </Field>
        <Field label={t("column.po.supplier-reference", "Supplier reference")} htmlFor="po-reference">
          <Input
            id="po-reference"
            value={draft.supplier_reference ?? ""}
            disabled={!editable}
            onChange={(event) => setDraft({ ...draft, supplier_reference: text(event.target.value) })}
          />
        </Field>
        <Field
          label={t("po.shipping", "Shipping")}
          htmlFor="po-shipping"
          error={editable && !shippingValid ? t("po.shipping.invalid", "An amount of zero or more.") : undefined}
        >
          <Input
            id="po-shipping"
            type="number"
            step="any"
            min={0}
            value={shipping}
            disabled={!editable}
            onChange={(event) => {
              const typed = event.target.value;
              const value = Number(typed);
              const { shipping_cost: _previous, ...rest } = fields;
              const usable = typed.trim() !== "" && value >= 0 && value !== Number(order.shipping_cost ?? 0);
              onEdit({ ...rest, shipping: typed, ...(usable ? { shipping_cost: value } : {}) });
            }}
          />
        </Field>
        <Field label={t("form.field.note", "Note")} htmlFor="po-note" className="sm:col-span-2 lg:col-span-3" hint={t("po.note.hint", "Printed on the order.")}>
          <Textarea
            id="po-note"
            rows={3}
            value={draft.note ?? ""}
            disabled={!editable}
            onChange={(event) => setDraft({ ...draft, note: text(event.target.value) })}
          />
        </Field>
      </div>
      {editable && (
        <div className="mt-4 flex items-center gap-3">
          <Button
            variant="primary"
            disabled={!dirty || !shippingValid || !orderDateValid || !requiredValid}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {t("button.save", "Save")}
          </Button>
          {dirty && (
            <Button variant="ghost" disabled={save.isPending} onClick={() => onEdit({})}>
              {t("button.discard", "Discard")}
            </Button>
          )}
        </div>
      )}
      <OrderLife order={order} />
    </Card>
  );
}

/**
 * The order's life so far, read-only: where it came from, when it went to the
 * approver and came back, when the supplier got it and when it was told to
 * ship. Only the steps that happened are shown.
 */
function OrderLife({ order }: { order: PurchaseOrderDTO }) {
  const { t } = useTranslation();
  const day = (stamp: string | null | undefined) => (stamp ? stamp.slice(0, 10) : null);
  const facts: Array<[string, string | null]> = [
    [
      t("po.life.origin", "Written"),
      {
        MANUAL: t("po.origin.manual", "Written by hand"),
        REPLENISHMENT: t("po.origin.replenishment", "From replenishment"),
        AUTOMATIC: t("po.origin.automatic", "Automatic draft"),
      }[order.origin ?? "MANUAL"],
    ],
    [t("po.life.submitted", "Sent for approval"), day(order.submitted_at)],
    [t("po.life.approved", "Approved"), day(order.approved_at)],
    [t("po.life.issued", "Issued"), day(order.issued_at)],
    [
      t("po.life.released", "Released to ship"),
      order.status === "ISSUED" && order.is_on_hold ? t("po.life.held", "Not yet, on hold") : (order.released_date ?? null),
    ],
    [t("po.life.closed", "Closed"), day(order.closed_at)],
    [t("po.life.total-cad", "Total in CAD"), order.total_cad == null ? null : formatCad(order.total_cad)],
  ];
  const shown = facts.filter(([, value]) => value !== null && value !== "");
  return (
    <div className="mt-6 border-t border-line pt-4">
      <h3 className="mb-3 text-[11px] font-medium uppercase tracking-wide text-ink-faint">{t("po.life.title", "History")}</h3>
      <dl className="grid gap-x-6 gap-y-2 text-[13px] sm:grid-cols-2 lg:grid-cols-3">
        {shown.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3 border-b border-line-soft pb-1.5">
            <dt className="text-ink-muted">{label}</dt>
            <dd className="font-medium tabular-nums text-ink">{value}</dd>
          </div>
        ))}
      </dl>
      {order.approval_note && (
        <p className="mt-3 text-[13px]">
          <span className="text-ink-muted">{t("po.life.approval-note", "Approver's note")}: </span>
          <span className="text-ink">{order.approval_note}</span>
        </p>
      )}
    </div>
  );
}

/** Back to draft, with the reason the writer needs to fix it. The server refuses an empty note. */
function SendBackDialog({
  open,
  orderId,
  onClose,
  onDone,
}: {
  open: boolean;
  orderId: number;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const [note, setNote] = useState("");
  useEffect(() => {
    if (open) setNote("");
  }, [open]);

  const send = useMutation({
    mutationFn: () => new PurchaseOrderClient(http).sendBack(orderId, note.trim()),
    onSuccess: async (updated) => {
      toast.success(t("po.sent-back.done", "Order {{n}} is back in draft.", { n: updated.number }));
      await onDone();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("po.send-back.title", "Send back to draft")}
      description={t("po.send-back.description", "Say what to change. The writer sees it at the top of the order.")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={note.trim() === ""} loading={send.isPending} onClick={() => send.mutate()}>
            {t("po.send-back", "Send back")}
          </Button>
        </>
      }
    >
      <Field label={t("po.send-back.note", "What to change")} htmlFor="po-send-back-note" required>
        <Textarea
          id="po-send-back-note"
          rows={4}
          value={note}
          placeholder={t("po.send-back.placeholder", "Split it over two trucks; the second can wait for November.")}
          onChange={(event) => setNote(event.target.value)}
        />
      </Field>
    </Dialog>
  );
}
